<template>
    <div class="comment-block">
        <div style="display: flex; justify-content: space-between">
            <div class="comment-block__user">
                <template v-if="props.comment.isUserComment && props.comment.createdBy">
                    <user-avatar
                        :placeholder="props.comment.createdBy.name.abbreviation ?? ''"
                        :url="props.comment.createdBy.avatar ? resizedToSizeUploadUrl(props.comment.createdBy.avatar, 'lg') : ''"
                        :alt="props.comment.createdBy.name.fullName"
                    />

                    <text-decorator :size="FontSize.sm">
                        {{ props.comment.createdBy.name }}
                    </text-decorator>
                </template>

                <template v-else-if="props.comment.isVirtualColleagueComment">
                    <virtual-colleague-avatar
                        size="lg"
                        :variant="currentLegacyCompany.virtualColleagueAvatar"
                    />
                    <flex-container
                        direction="column"
                        size="auto"
                        gap="xs"
                    >
                        <text-decorator :size="FontSize.sm">
                            {{ currentLegacyCompany.virtualColleagueName }}
                        </text-decorator>
                        <text-decorator :color="Color.gray400" :size="FontSize.xs">
                            Virtuele Collega
                        </text-decorator>
                    </flex-container>
                </template>

                <template v-else>
                    <keyvue-text-logo style="height: 20px;" />
                </template>
            </div>

            <chip-label
                v-if="props.comment.lead"
                size="lg"
                :border-color="null"
                :background-color="Color.gray100"
            >
                <svg-icon
                    :icon="IconPipeline"
                    :size="IconSize.sm"
                />
                {{ props.comment.lead.pipeline.name }}
            </chip-label>
        </div>

        <div :class="textClasses">
            <div v-if="props.pinned" class="comment-block__pin">
                <svg-icon
                    :icon="IconPushPin"
                    :color="props.pinColor"
                />
            </div>

            <!-- eslint-disable-next-line vue/no-v-html -->
            <p class="comment-block__font-style" v-html="commentContent" />
            <div v-if="props.comment.createdBy?.equals(client.session.currentUser)">
                <collapse-box-decorator horizontal-position="right">
                    <template #default>
                        <svg-icon
                            :icon="IconMore"
                            :size="IconSize.md"
                            :color="Color.brandGreen"
                        />
                    </template>
                    <template #content>
                        <collapse-option-container>
                            <collapse-option
                                v-if="props.deleteComment"
                                @click="props.deleteComment(props.comment.id)"
                            >
                                <svg-icon
                                    :icon="IconDelete"
                                    :size="IconSize.md"
                                    :color="Color.red500"
                                />
                                <span>Verwijderen</span>
                            </collapse-option>
                        </collapse-option-container>
                    </template>
                </collapse-box-decorator>
            </div>
        </div>

        <div v-if="!hideFooter" style="display: flex; justify-content: space-between">
            <div class="comment-block__visibility">
                <svg-icon
                    :icon="IconVisibility"
                    :size="IconSize.sm"
                />
                <text-decorator
                    :size="FontSize.xs"
                >
                    {{ props.comment.visibilityLabel }}
                </text-decorator>
            </div>
            <text-decorator
                :size="FontSize.xs"
            >
                {{ formatDateTime(props.comment.createdAt) }} uur
            </text-decorator>
        </div>
    </div>
</template>

<script setup lang="ts">
    import type { Comment, CommentId } from '@keyvue/one-api-client';

    import {
        Color,
        IconSize,
        FontSize,
        SvgIcon,
        UserAvatar,
        CollapseOptionContainer,
        CollapseOption,
        TextDecorator,
    } from '@keyvue/one-ui';

    import { computed } from 'vue';
    import { formatDateTime } from '@/utils/date';
    import { resizedToSizeUploadUrl } from '@/utils/resize';
    import useClient from '@composables/useClient';
    import KeyvueTextLogo from '@assets/logo-text.svg?component';

    import IconPipeline from '@keyvue/one-material-icons/icons/filter_alt.svg?component';
    import IconMore from '@keyvue/one-material-icons/icons/more_vert.svg?component';
    import IconDelete from '@keyvue/one-material-icons/icons/delete.svg?component';
    import IconVisibility from '@keyvue/one-material-icons/icons/visibility.svg?component';
    import IconPushPin from '@keyvue/one-material-icons/icons/push_pin.svg?component';

    interface Props {
        comment: Comment,
        hideFooter?: boolean,
        pinned?: boolean,
        pinColor?: Color,
        color?: Color,
        deleteComment?: (commentId: CommentId) => Promise<void>,
    }

    const props = withDefaults(defineProps<Props>(), {
        hideFooter: false,
        pinned: false,
        pinColor: Color.orange500,
        color: Color.gray100,
        deleteComment: undefined,
    });

    const { client, currentLegacyCompany } = useClient();
    const commentContent = computed(() => (props.comment.comment));

    const textClasses = computed(() => ({
        'comment-block__text': true,
        'comment-block__text--pinned': props.pinned,
        [`comment-block__text--background-color-${props.color}`]: true,
    }));
</script>

<style scoped lang="scss">
:global(.comment-block .comment-block__text > p a) {
    color: color(brand-green);
}

.comment-block {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: size(sm);

    &__user {
        display: flex;
        align-items: center;
        gap: size(sm);
    }

    &__text {
        display: inline-flex;
        position: relative;
        padding: size(md) size(xl) size(md) size(md);
        border-radius: size(sm);
        border-top-left-radius: unset;
        line-height: size(md);
        width: fit-content;
        @include addColors(background-color);

        & > div {
            position: absolute;
            right: size(xs);
            top: size(sm);
        }
    }

    &__pin {
        position: absolute;
        top: 0;
        right: 0;
    }

    &__visibility {
        display: flex;
        align-items: center;
        gap: size(xs);
    }
}

.comment-block__font-style {
    font-size: font-size(sm)
}
</style>
